<template>
  <div v-if="showModal" class="modal-backdrop">
    <div class="modal-content">
      <!-- Estado: App instalada pero abierta en navegador -->
      <div v-if="installState === 'already-installed'" class="already-installed-state">
        <div class="success-icon">📱</div>
        <h2>¡Tienes la aplicación instalada!</h2>
        <p>Esta aplicación ya está instalada en tu dispositivo.</p>
        <p><strong>Para una mejor experiencia, ábrela desde el ícono en tu pantalla de inicio.</strong></p>
        <button @click="dismissModal" class="install-btn" style="margin-top: 1rem;">
          Entendido
        </button>
      </div>

      <!-- Estado normal: Solicitud de instalación -->
      <div v-else-if="installState === 'prompt'" class="install-prompt">
        <img src="/logo_512.png" alt="App Logo" class="app-logo">
        <h2>Instalar Aplicación</h2>
        <p>Para una mejor experiencia, instala la aplicación en tu dispositivo.</p>
        <div class="modal-actions">
          <button @click="handleInstall" class="install-btn">
            Instalar App
          </button>
          <button @click="dismissModal" class="dismiss-btn">
            Ahora no
          </button>
        </div>
      </div>

      <!-- Estado de instalación: Animación de carga -->
      <div v-else-if="installState === 'installing'" class="installing-state">
        <div class="loading-spinner"></div>
        <h2>Instalando...</h2>
        <p>Por favor, espera mientras instalamos la aplicación</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue';
import { usePWAInstall } from '@/composables/usePWAInstall';

const { isInstallable, isInStandaloneMode, showInstallPrompt, justInstalled } = usePWAInstall();
const showModal = ref(false);
const installState = ref<'prompt' | 'installing' | 'already-installed'>('prompt');
const dismissedInstallPrompt = ref(false);

// Computed para determinar cuándo mostrar cada modal
const shouldShowAlreadyInstalledModal = computed(() => {
  return justInstalled.value;
});

const shouldShowInstallPromptModal = computed(() => {
  return isInstallable.value && !dismissedInstallPrompt.value && !isInStandaloneMode.value;
});

// Lógica principal para mostrar modales
watchEffect(() => {
  if (shouldShowAlreadyInstalledModal.value) {
    // Modal: "Ya está instalada" (justo después de instalar)
    console.log('Mostrando modal de ya instalada - PWA recién instalada');
    showModal.value = true;
    installState.value = 'already-installed';
  } else if (shouldShowInstallPromptModal.value) {
    // Modal: "Instalar aplicación" (cuando se puede instalar)
    console.log('Mostrando modal de instalación - PWA se puede instalar');
    setTimeout(() => {
      showModal.value = true;
      installState.value = 'prompt';
    }, 1500);
  } else {
    showModal.value = false;
  }
});

// Watch for installation completion
watchEffect(() => {
  if (installState.value === 'installing' && justInstalled.value) {
    // Cambiar a modal de "ya instalada"
    installState.value = 'already-installed';
  }
});

const handleInstall = async () => {
  try {
    installState.value = 'installing';
    const installed = await showInstallPrompt();

    if (installed) {
      console.log('Usuario aceptó la instalación');
      // Esperar a que se complete la instalación
      setTimeout(() => {
        showModal.value = false;
        installState.value = 'prompt';
      }, 3000);
    } else {
      console.log('Usuario canceló la instalación');
      installState.value = 'prompt';
    }
  } catch (error) {
    console.error('Error al instalar la app:', error);
    installState.value = 'prompt';
  }
};

const dismissModal = () => {
  if (installState.value === 'prompt') {
    dismissedInstallPrompt.value = true;
  } else if (installState.value === 'already-installed') {
    // Al cerrar la modal de "ya instalada", resetear el flag
    justInstalled.value = false;
  }

  showModal.value = false;
  installState.value = 'prompt';
};

// Reset dismissed states when PWA status changes
watchEffect(() => {
  if (!isInstallable.value && !justInstalled.value) {
    // Si no es instalable y no está recién instalada, resetear dismissals
    dismissedInstallPrompt.value = false;
  }
});
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-width: 90%;
  width: 400px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  transition: all 0.3s ease;
}

.app-logo {
  width: 70px;
  height: 70px;
  margin-bottom: 1rem;
}

.modal-content h2 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.modal-content p {
  font-size: 1rem;
  color: #555;
  margin-bottom: 1.5rem;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.install-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.install-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.dismiss-btn {
  background: transparent;
  color: #777;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dismiss-btn:hover {
  background-color: #f0f0f0;
}

/* Estados de instalación */
.installing-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Spinner de carga */
.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Icono de éxito */
.success-icon {
  width: 70px;
  height: 70px;
  margin-bottom: 1rem;
  font-size: 3rem;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Estados de animación para las transiciones */
.install-prompt,
.installing-state,
.already-installed-state {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .modal-content {
    width: 90%;
    padding: 1.5rem;
  }

  .modal-content h2 {
    font-size: 1.3rem;
  }

  .app-logo {
    width: 60px;
    height: 60px;
  }
}
</style>
